import {IOwayoDataModel} from "../model";
import {EventListener, EventVerteiler} from "@k/global/eventVerteiler";
import {Color} from "../colorObject";
import {ColorCodeAliasTranslator} from "./colorcodeAliasHandler";
import {ColorUtils} from "../colorUtils";
import {ChromoLogo, Logo, SelectableObject, StockLogo, TextObject} from "../selectableObjects";

import {SelObTypes} from "../SelObTypes";
import {PatternInEntwurf} from "../pattern/patternEntwurfsobjektInteractor";
import {PalettenLoader} from "../palettenLoader";

import {ResourceInvalidationTriggers} from "@k/global/resourcemanager/resourceManager";
import {CONSTANTS} from "../meta_data";
import {SELOBEVENTNAMES} from "@k/global/GlobalEventnames";
import {
    ArtikelFarbVariante,
    ColorCodeAbhaengigkeitsArten,
    ColorCodeFarbKombiBeziehung,
    ColorCodeFromDBInfusedWithEntwurfColorData,
    PalettenEintragFromDB
} from "../databaseInterfaceClasses";
import {VIEWERFEATURE} from "../debugMenu";
import globalsObject from "../globalsObject";
import {PATTERN_EVENTNAMES} from "../pattern/patternFactory";
import {arrayElementByKeyValue, uniqueValues} from "@k/global/arrayHelper";
import {usePalettenClusterStore} from "@k/vue/store/palettenClusterStore";
import {afterBrowserRepaintDo} from "@k/global/globalUtils";

export type EntwurfTransferStorageColor = {
    colorCode,
    colorCodeToDisplay,
    colorValue,
    palette,
    palettenCluster,
    blendToBackground,
}

export interface FarbvariantenController {
    changeToFarbvariante: (variante: string) => void;
    getCurrentFarbvariante: () => ArtikelFarbVariante[];
    setColorsFarbvariante: (variante: ArtikelFarbVariante[]) => void;
    getFirstColorFromFarbvariante: () => ArtikelFarbVariante
}

export class ColorController extends EventListener implements FarbvariantenController {

    public model: IOwayoDataModel;
    private readonly tracker: { trackPageview } | null = null

    public constructor(model: IOwayoDataModel, tracker: { trackPageview } | null = null) {
        super();
        this.model = model;
        this.tracker = tracker;
        const $this = this;
        this
            .addEventListener("colorChangedInColorPicker", function(data){
                globalsObject.loopTracker.trackSelectColorEvent(data.palette, data.value);
                $this.changeColor(data.colorCode, data.value, data.palette, "byUser");
            })
            .addEventListener("broadcastUsedColorsEvent", () => this.sendUsedColorsToViewComponents())
            .addEventListener(PATTERN_EVENTNAMES.CREATED, (data: {
                stockLogo: StockLogo
            }) => this.setPatternFlag(data.stockLogo))
            .addEventListener(PATTERN_EVENTNAMES.DELETED, (data) => this.removePatternFlag(data.colorCodeNr))
            .addEventListener("initNewEntwurfEvent", () => globalsObject.resourceManager.executeInvalidation("entwurfColorInit"))
            .addEventListener("initEntwurfColorCodesEvent", function (data) {                // if(DEBUG){console.log(data.colorCodes);}

                $this.initEntwurfColorCodes(data.colorCodes, data.ColorCodeFarbVorbelegungen, data.ColorCodeAbhaengigkeiten, data.entwurfTransferStorage, data.quickFixDontCareAboutColorValuesIsSetWhenLoadingSavedEntwurf);
                $this.setColorVisibleStatus();
                globalsObject.resourceManager.registerLoadedResourceWithoutData("entwurfcolorsLoaded", "entwurfColorInit");
            })
            .addEventListener("changeProductColorToColorName", colorname => this.changeProductColorToColorName(colorname))
            .addEventListener("getPalettenClusterForColorCode", colorcode => this.getPalettenClusterForColorCode(colorcode))
    }

    protected trackPageView(page) {
        if (this.tracker === null) {
            return;
        }
        this.tracker.trackPageview(page);
    }

    public getCurrentFarbvariante() {
        return this.model.getCurrentOwayoProduct().Farbvarianten[this.model.entwurf.getFarbvariante()]
    }

    public setColorsFarbvariante = farbvariante => farbvariante.map(color => this.changeColor(color.colorcode, color.RGBHex, color.palette));
    public broadcastInitColorsEvent = () => EventVerteiler.broadcast("initColorsEvent", {colorCodes: this.model.entwurf.getColorObjects()});
    private getUsedColorsInDesign = () => uniqueValues(this.model.entwurf.getColorObjects().filter(c => c.isVisible).map(c => c.getValueAsHex()));

    private setPatternFlag(stockLogo: StockLogo) {
        const colorObject = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCodeNr", stockLogo.clampToColor) as Color;
        colorObject.hatPattern = true;
    }

    private removePatternFlag(colorCodeNr) {
        const colorObject = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCodeNr", colorCodeNr) as Color;
        colorObject.hatPattern = false;
    }

    public colorIsVisible(colorCode): boolean {
        const color = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", colorCode);
        return color === null ? false : (color as Color).isVisible;
    }

    public colorToDisplayIsVisible(colorCode): boolean {
        const color = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", colorCode);
        if (color === null) {
            return false;
        }
        return this.colorIsVisible(color.colorCodeToDisplay);
    }

    private ifColorObjectIsOwayoOStopShowingOwayoLogosAsHighestLayer(changedColorObject) {
        if (changedColorObject.isDependent === true && this.model.entwurf.isOwayoLogosOnTop() === true && changedColorObject.colorCode.indexOf("OO") > -1) {
            this.model.entwurf.setOwayoLogosOnTop(false);
            EventVerteiler.broadcast("recreateAllShadersEvent");
        }
    }

    public changeToFarbvariante(variantenName: string) {
        let farbvariante = this.model.getCurrentOwayoProduct().Farbvarianten[variantenName];
        if (typeof farbvariante === "undefined") {
            variantenName = this.getFarbvariantenNameByFirstColorValue(variantenName) as string;
            farbvariante = this.model.getCurrentOwayoProduct().Farbvarianten[variantenName];
        }
        this.setColorsFarbvariante(farbvariante);

        this.model.entwurf.setFarbvariante(variantenName);
        EventVerteiler.broadcast("setSelectedFarbvarianteEvent", variantenName);
    }

    public getFirstColorFromFarbvariante() {
        const variante = this.model.getCurrentOwayoProduct().Farbvarianten[this.model.entwurf.getFarbvariante()];
        return variante[0];
    }

    private getFarbvariantenNameByFirstColorValue(colorValue) {
        const farbvarianten = this.model.getCurrentOwayoProduct().Farbvarianten;
        for (const fv in farbvarianten) {
            const f = arrayElementByKeyValue(farbvarianten[fv], "colorcode", "A");
            if (f.RGBHex === colorValue.replace("#", "")) {
                return fv;
            }
        }
        return null;
    }

    public changeColor(colorCode, colorHexValue, palettenName, byUser = "no", checkAliasConnection = true) {
        const palettenStore = usePalettenClusterStore();
        const $this = this;
        const colorObject = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", colorCode) as Color;
        const PalettenFarben = palettenStore.getPalette(palettenName);
        const palettenFarbe = PalettenFarben !== null ? arrayElementByKeyValue(PalettenFarben, "RGBHex", colorHexValue.replace("#", "")) : null;
        const TexturenImage = palettenFarbe !== null ? palettenFarbe.TexturenImage : "";

        if (colorObject === null) {
            return;
        }
        let masterColor;
        this.ifColorObjectIsOwayoOStopShowingOwayoLogosAsHighestLayer(colorObject);
        if (byUser === "byUser") {
            this.model.entwurf.setChangedSinceSave(true);
        }

        if (colorObject.isHideable) {
            masterColor = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", colorObject.dependentOn);
            colorObject.setValueAndBlendToBackgroundFlag(colorHexValue, masterColor);
        } else {
            colorObject.setValueFromHex(colorHexValue, palettenName);
        }

        this.model.getCurrentOwayoProduct().farbBeziehungenZwischenColorCodes.filter(b => b.colorCode === colorCode).forEach(colorCodeFarbKombiBeziehung => {
            this.setAbhaengigUeberFarbabhaengigkeitsregel(colorCodeFarbKombiBeziehung);
        })


        EventVerteiler.broadcast("colorChangedEvent", {
            colorCode: colorCode,
            colorHexValue: colorHexValue,
            byUser: byUser
        });
        this.sendUsedColorsToViewComponents();
        this.changeVorbelegungsChromoLogoColorfNecessary(colorCode);

        if (TexturenImage !== "") {
            EventVerteiler.broadcast("loadBildPalettenTexturenImage", {
                colorCode: colorCode,
                TexturenImage: TexturenImage
            });
        }

        if (byUser !== "no") {
            this.trackPageView("konfiaccess/3Dfarbegeaendert.htm");
        }
        if (checkAliasConnection) {
            globalsObject.resourceManager.createTask("changeConnectedColors")
                .discardAfterExecution()
                .when("produkt.php", "loaded")
                .thenDo(function () {
                    ColorCodeAliasTranslator.getColorcodesWithAlias(colorObject.colorCodeToDisplay, $this.model.entwurf.getDesign())
                        .filter(c => c.colorCode !== colorObject.colorCode)
                        .forEach(c => $this.changeColor(c.colorCode, colorHexValue, palettenName, "no", false));
                });
        }

    }

    public setAbhaengigUeberFarbabhaengigkeitsregel(colorCodeFarbKombiBeziehung: ColorCodeFarbKombiBeziehung) {
        colorCodeFarbKombiBeziehung.farbkombis.forEach(k => {
            globalsObject.resourceManager.createTask("farbkombisetColorFor" + colorCodeFarbKombiBeziehung.colorCode + k.NameImWeb)
                .discardAfterExecution()
                .when("palette_" + k.palette, "loaded")
                .andWhen("palette_" + k.folgePalette, "loaded")
                .thenDo(() => {
                    const palettenStore = usePalettenClusterStore();
                    const geberColorCode = this.model.entwurf.getColorObjects().find(c => c.colorCode === colorCodeFarbKombiBeziehung.colorCode) as Color | undefined;
                    const geberPalette = palettenStore.getPalette(k.palette);
                    const geberPalettenFarbe = geberPalette.find(pc => pc.NameImWeb === k.NameImWeb);
                    if (geberPalettenFarbe === undefined || geberColorCode === undefined || geberPalettenFarbe.RGBHex !== geberColorCode.getValueAsHex()) {
                        return;
                    }
                    const nehmerPalette = palettenStore.getPalette(k.folgePalette);
                    const nehmerPalettenFarbe = nehmerPalette.find(pc => pc.NameImWeb === k.folgenameImWeb);
                    if (nehmerPalettenFarbe === undefined) {
                        return;
                    }
                    this.changeColor(colorCodeFarbKombiBeziehung.folgeColorCode, nehmerPalettenFarbe.RGBHex, k.folgePalette, "no", false);
                });
        })

    }

    private getUsedColorsInTextObjects() {
        return (this.model.entwurf.getTextObjects() as TextObject[]).reduce(
            function (carry, to) {
                carry.push(ColorUtils.decToHex(to.colorValue));
                carry.push(ColorUtils.decToHex(to.outlineColorValue as number));
                return carry;
            },
            [] as string[]
        )
    }

    private getUsedColorsInChromoLogo() {
        return this.model.entwurf.getLogoObjects().reduce(function (carry, logo: Logo | ChromoLogo) {
                if (!SelObTypes.isEinfaerbbaresLogo(logo.type)) {
                    return carry;
                }
                carry = carry.concat((logo as ChromoLogo).colors.map(c => ColorUtils.decToHex(c.colorIntValue)));
                return carry;
            }
            , [] as string[]);
    }

    private getUsedColorsInPatterns() {
        return this.model.entwurf.getPatterns().reduce(function (carry, pattern: PatternInEntwurf) {
                carry = carry.concat(pattern.colors.map(c => ColorUtils.decToHex(c.colorIntValue)));
                return carry;
            }
            , [] as string[]);
    }

    public sendUsedColorsToViewComponents() {
        const usedColors = uniqueValues(
            this.getUsedColorsInDesign()
                .concat(this.getUsedColorsInChromoLogo())
                .concat(this.getUsedColorsInTextObjects())
                .concat(this.getUsedColorsInPatterns())
        );

        EventVerteiler.broadcast("updateUsedColorsEvent", {usedColors: usedColors});
    }

    public changeVorbelegungsChromoLogoColorfNecessary(changedColorCode) {

        const $this = this;
        const newColor = this.model.entwurf.getColorObjectByColorCode(changedColorCode) as Color;
        const newColorIntValue = newColor.colorValue;

        this.model.entwurf.getLogoObjects().forEach(function (logo: SelectableObject) {
            if (SelObTypes.isEinfaerbbaresLogo(logo.type) && logo.isVorbelegung) {
                if (globalsObject.owayoJSViewer.getColorCodeUnderPartSelOb(logo.id) === changedColorCode) {
                    if ((logo as ChromoLogo).farbkombinationen.length > 0) {
                        $this.selectChromoLogoFarbkombination(logo as ChromoLogo, newColorIntValue);
                    } else {
                        $this.checkChromoLogoColorsAndChangeIfNecessary(logo as ChromoLogo, newColorIntValue);
                    }
                }
            }
        });
    }

    private selectChromoLogoFarbkombination(logo: ChromoLogo, newColorIntValue: number) {

        for (let i = 0, lenI = logo.farbkombinationen.length; i < lenI; i++) {
            if (ColorController.colorHasEnoughContrastForColorKombi(newColorIntValue, logo.farbkombinationen[i])) {
                this.assignColorKombiToLogo(logo.farbkombinationen[i], logo);
                return;
            }
        }
        this.assignColorKombiToLogo(logo.farbkombinationen[0], logo);
    }

    private static colorHasEnoughContrastForColorKombi(newColorIntValue, farbKombination) {
        return farbKombination.reduce(function (enoughConstrastSoFar, kombi) {
            const constrast = ColorController.getDeltaEFromIntValues(newColorIntValue, kombi.colorIntValue);
            return enoughConstrastSoFar && (constrast >= CONSTANTS.DELTA_E_THRESHHOLD);
        }, true);
    }

    private assignColorKombiToLogo(farbKombi, logo: ChromoLogo) {
        logo.colors = logo.colors.map(function (color) {
            for (let i = 0, len = farbKombi.length; i < len; i++) {
                if (farbKombi[i].colorCode === logo.colors[i].colorCode) {
                    color.colorIntValue = farbKombi[i].colorIntValue;
                }
            }
            return color;
        });

        logo.createCurrentSVG();
        EventVerteiler.broadcast(
            SELOBEVENTNAMES.CHANGED, {
                selOb: logo,
                changedProperties: ["colors"]
            }
        );
    }

    private checkChromoLogoColorsAndChangeIfNecessary(logo: ChromoLogo, newColorIntValue: number) {
        logo.colors.forEach(function (c) {
            const luminanceDelta = ColorController.getLuminanceDeltaFromIntValues(newColorIntValue, c.colorIntValue);
            if (luminanceDelta < CONSTANTS.LUMINANCE_THRESHHOLD) {
                c.colorIntValue = ColorController.getContrastColorFromPaletteToIntValue(c.palette, newColorIntValue);
                logo.createCurrentSVG();
                EventVerteiler.broadcast(
                    SELOBEVENTNAMES.CHANGED, {
                        selOb: logo,
                        changedProperties: ["colors"]
                    }
                );
            }
        });
    }

    private static getContrastColorFromPaletteToIntValue(palettenName: string, newColorIntValue: number) {
        let maxDistance = 0, chosenIndex = 0;
        const palettenStore = usePalettenClusterStore();
        let palette = palettenStore.getPalette(palettenName);
        if (palette === null) {
            palette = palettenStore.getPalette(CONSTANTS.STANDARD_PALETTE);
        }
        const anzColors = palette.length;
        const rgbObjectOfNewColor = ColorUtils.hexToRgbObject(ColorUtils.decToHex(newColorIntValue));
        const luminanceOfNewColor = ColorUtils.getLuminanceOfRgbObject(rgbObjectOfNewColor);
        for (let i = 0; i < anzColors; i++) {
            const distance = Math.abs(luminanceOfNewColor - ColorUtils.getLuminanceOfRgbObject(ColorUtils.hexToRgbObject(palette[i].RGBHex)));
            if (distance >= maxDistance) {
                maxDistance = distance;
                chosenIndex = i;
            }
        }

        return ColorUtils.hexToDec(palette[chosenIndex].RGBHex);
    }

    public checkAllColorsAndChangeChromoLogoIfNecessary() {
        this.model.entwurf.getColorObjects().forEach(colorObject => this.changeVorbelegungsChromoLogoColorfNecessary(colorObject.colorCode));
    }

    colorCouldNotBeSet(color: Color) {
        const $this = this;
        globalsObject.resourceManager.createTask("reloadColor" + color.colorCode)
            .discardAfterExecution()
            .invalidAfter(ResourceInvalidationTriggers.PRODUCTCHANGE)
            .when(PalettenLoader.getPalettenClusterResourceName(color.palettenCluster), "loaded")
            .thenDo(function () {
                $this.changeColor(
                    color.colorCode,
                    color.getColorByPercentages(
                        PalettenLoader.getFlattenedPalettenCluster(color.palettenCluster) as PalettenEintragFromDB[]
                    ),
                    color.palette
                );
            });
    }

    setColorVisibleStatus() {
        const shouldBeVisible = color => VIEWERFEATURE.SHOW_ALL_COLORS || (!color.isDependent && color.inKonfi && !color.multipleMasters);
        const colorObjects = this.model.entwurf.getColorObjects();
        colorObjects.forEach((color: Color) => color.isVisible = shouldBeVisible(color));
        this.setMainColorVisibilityAccordingToBaseColorArray();
        this.setVisibilityToNotVisibleForAusgeblendeteBaseColors();
        this.setColorOutlinesVisibility();
        this.hideColorsThatAreJoinedByAlias();
        this.broadcastInitColorsEvent();
    }

    private setVisibilityToNotVisibleForAusgeblendeteBaseColors() {
        const design = arrayElementByKeyValue(this.model.getCurrentOwayoProduct().possibleDesigns, "Design", this.model.entwurf.getDesign());
        if (design === null) {
            return;
        }
        design.ausgeblendeteColorCodes.forEach(colorCodeObject => {
            const color = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", colorCodeObject.ColorCode);
            if (color !== null) {
                color.isVisible = false;
            }
        })
    }

    private hideColorsThatAreJoinedByAlias() {
        this.model.entwurf.getColorObjects().forEach(function (c: Color) {
            if (c.colorCodeToDisplay !== c.colorCode) {
                c.isVisible = false;
            }
        })
    }

    private setMainColorVisibilityAccordingToBaseColorArray() {
        const design = arrayElementByKeyValue(this.model.getCurrentOwayoProduct().possibleDesigns, "Design", this.model.entwurf.getDesign());
        const baseColors = design.baseColors || [];
        CONSTANTS.MAINCOLORCODES.forEach(ccode => {
            const c = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", ccode);
            if (c === null) {
                return;
            }
            c.isVisible = baseColors.indexOf(ccode) > -1 && !c.isDependent;
        });
    }

    private static isColorUsedAsAlias(colorCode, colorList) {
        return colorList.filter(color => color.colorCodeToDisplay === colorCode && color.colorCode !== colorCode).length > 0;
    }

    private setColorOutlinesVisibility() {
        const design = arrayElementByKeyValue(this.model.getCurrentOwayoProduct().possibleDesigns, "Design", this.model.entwurf.getDesign());
        const arrOutlinedColorCodes = design.ColorCodesWithOutline.split(',').map(e => e.trim());

        for (let i = 0; i < this.model.entwurf.getColorObjects().length; i++) {
            const color = this.model.entwurf.getColorObjects()[i];
            if (color.isOutlineColorCode()) {
                const OutlinedColorCode = color.colorCode.replace('_O', '');
                color.isVisible = (arrOutlinedColorCodes.indexOf(OutlinedColorCode) > -1 || ColorController.isColorUsedAsAlias(color.colorCode, this.model.entwurf.getColorObjects()));
            }
        }
    }

    private static isColorValueInPalettenCluster(colorValue, palettenCluster) {
        for (const palette in palettenCluster) {
            const color = arrayElementByKeyValue(palettenCluster[palette], "RGBHex", ColorUtils.decToHex(colorValue));
            if (color !== null) {
                return true;
            }
        }
        return false;
    }

    public setColorsFromTransferStorage(colorObjects: Color[]) {
        for (let i = colorObjects.length - 1; i > -1; i--) {
            const color = arrayElementByKeyValue(this.model.entwurf.getColorObjects(), "colorCode", colorObjects[i].colorCode);
            if (color !== null) {
                const palettenStore = usePalettenClusterStore();
                const palettenCluster = palettenStore.getPalettenCluster(color.palettenCluster)
                // const resource = PalettenLoader.getPalettenClusterResource(color.palettenCluster);
                if (color.palettenCluster === colorObjects[i].palettenCluster) {
                    color.colorValue = colorObjects[i].colorValue;
                    color.palette = colorObjects[i].palette;
                    color.palettenCluster = colorObjects[i].palettenCluster;
                    if (colorObjects[i].blendToBackground === true) {
                        color.blendToBackground = true;
                    }
                    //ColorCodes, die es nicht gibt, bleiben im TransferStorage, falls beim nächsten productChange der ColorCode wieder da ist.
                    colorObjects.splice(i, 1);
                } else {
                    if (ColorController.isColorValueInPalettenCluster(colorObjects[i].colorValue, palettenCluster)) {
                        color.colorValue = colorObjects[i].colorValue;
                        if (colorObjects[i].blendToBackground === true) {
                            color.blendToBackground = true;
                        }
                        //ColorCodes, die es nicht gibt, bleiben im TransferStorage, falls beim nächsten productChange der ColorCode wieder da ist.
                        colorObjects.splice(i, 1);
                    }
                }
            }
        }
        return colorObjects;
    }

    setColorsToColorCodeAliases() {
        const $this = this;
        $this.model.entwurf.getColorObjects().map(function (color: Color) {
            if (color.colorCode !== color.colorCodeToDisplay) {
                const displayColor = arrayElementByKeyValue($this.model.entwurf.getColorObjects(), "colorCode", color.colorCodeToDisplay);
                if (displayColor !== null) {
                    color.colorValue = displayColor.colorValue;
                    EventVerteiler.broadcast(PATTERN_EVENTNAMES.SETPATTERN_TO_ALIAS, {
                        receiverColorCodeNr: color.colorCodeNr,
                        donorColorCodeNr: displayColor.colorCodeNr
                    });
                }
                globalsObject.owayoJSViewer.changeColor(color.colorCode, ColorUtils.decToHex(color.colorValue));

            }
        });
    }

    initEntwurfColorCodes(colorsFromDB: ColorCodeFromDBInfusedWithEntwurfColorData[], ColorCodeFarbVorbelegungen, ColorCodeAbhaengigkeiten, entwurfTransferStorage?, quickFixDontCareAboutColorValuesIsSetWhenLoadingSavedEntwurf = false) {
        const aliases = ColorCodeAliasTranslator.setAliases(
            colorsFromDB.map(c => this.constructColorFromColorData(c, ColorCodeFarbVorbelegungen, ColorCodeAbhaengigkeiten, quickFixDontCareAboutColorValuesIsSetWhenLoadingSavedEntwurf)),
            globalsObject.controller.model.entwurf.getDesign()
        ) as Color[];
        this.model.entwurf.setColorObjects(aliases);

        if (typeof (entwurfTransferStorage) !== "undefined" && typeof (entwurfTransferStorage.colorObjects) !== "undefined") {
            this.setColorsFromTransferStorage(entwurfTransferStorage.colorObjects);
        }
        this.setColorsToColorCodeAliases();
        this.model.entwurf.setDependencyAndDependentColorValues(this.model.getCurrentOwayoProduct().farbBeziehungenZwischenColorCodes);

        this.model.getCurrentOwayoProduct().farbBeziehungenZwischenColorCodes.forEach(fk => {
            this.setAbhaengigUeberFarbabhaengigkeitsregel(fk)
        })
    }

    private static getColorWithMinDeviationFromPalettenCluster(colorIntValue, palettenClusterName) {
        const palettenClusterStore = usePalettenClusterStore();
        const resource = palettenClusterStore.getPalettenCluster(palettenClusterName);
        let paletteWithMinDeltaE = "";
        let minDeltaE = Infinity;
        let newColorHexValue = ColorUtils.decToHex(colorIntValue);
        for (const palette in resource) {
            const minData = ColorUtils.getColorWithMinDeviationFromPaletteInclMinData(colorIntValue, resource[palette]);
            if (Math.abs(minData.minDeltaE) < Math.abs(minDeltaE)) {
                minDeltaE = minData.minDeltaE;
                paletteWithMinDeltaE = palette;
                newColorHexValue = minData.colorHexValue;
            }
        }
        return {palette: paletteWithMinDeltaE, colorHexValue: newColorHexValue};
    }

    private createColorCouldNotBeSetTask(colorData, provisionalColorValue) {
        const $this = this;
        globalsObject.resourceManager.createTask("colorCouldNotBeSet" + colorData.colorCode)
            .invalidAfter("colorValueWasSet" + colorData.colorCode)
            .invalidAfter("colorValueWasSet")
            .discardAfterExecution()
            .whenMultipleRequirementsMet(PalettenLoader.getPalettenClusterTaskPrerequisites([colorData.PalettenCluster]))
            .thenDo(function () {
                const nearestColor = ColorController.getColorWithMinDeviationFromPalettenCluster(provisionalColorValue, colorData.PalettenCluster);
                $this.changeColor(colorData.colorCode, nearestColor.colorHexValue, nearestColor.palette, "no");


            });
    }

    private constructColorFromColorData(colorData: ColorCodeFromDBInfusedWithEntwurfColorData, ColorCodeFarbVorbelegungen, ColorCodeAbhaengigkeiten, quickFixDontCareAboutColorValuesIsSetWhenLoadingSavedEntwurf = false) {
        const colorCode = colorData.colorCode;
        const shouldInitiallyBeInvisible = colorCode.search(/^[ABCDE]$/) > -1 || colorData.farbAbhaengigkeitsArt !== ColorCodeAbhaengigkeitsArten.NONE;
        let colorValue = ColorCodeFarbVorbelegungen[colorData.colorCode];
        let palette = "";
        const ccAbhaengigkeit = arrayElementByKeyValue(ColorCodeAbhaengigkeiten, "ColorCode", colorData.colorCode);
        if (quickFixDontCareAboutColorValuesIsSetWhenLoadingSavedEntwurf === false) {
            const flattenedPalettenCluster = PalettenLoader.getFlattenedPalettenCluster(colorData.PalettenCluster);
            if (flattenedPalettenCluster === null) {
                if (colorData.farbAbhaengigkeitsArt === ColorCodeAbhaengigkeitsArten.NONE) {
                    this.createColorCouldNotBeSetTask(colorData, colorValue);
                }
            } else {
                const nearestColor = ColorController.getColorWithMinDeviationFromPalettenCluster(colorValue, colorData.PalettenCluster);
                colorValue = ColorUtils.hexToDec(nearestColor.colorHexValue);
                palette = nearestColor.palette;
            }
        }

        return new Color(
            {
                colorCode: colorCode,
                palette: palette,
                palettenCluster: colorData.PalettenCluster,
                isVisible: (!shouldInitiallyBeInvisible),
                colorCodeNr: parseInt(colorData.ColorCodeNr, 10),
                farbname: colorData.Farbname === null ? colorCode : colorData.Farbname,
                blackWhiteOnly: colorData.NurSchwarzWeiss,
                isDependent: colorData.farbAbhaengigkeitsArt !== ColorCodeAbhaengigkeitsArten.NONE,
                inKonfi: colorData.InKonfi,
                isHideable: colorData.IsHideable,
                groupID: parseInt(colorData.GroupID, 10),
                colorValue: colorValue,
                dependentOn: ccAbhaengigkeit === null ? null : ccAbhaengigkeit.MasterColorcode,
                multipleMasters: ccAbhaengigkeit === null ? null : ccAbhaengigkeit.VerteilungUeberPercentages,
                percentages: ccAbhaengigkeit === null ? null : typeof (ccAbhaengigkeit.Percentages) === "undefined" ? {} : ccAbhaengigkeit.Percentages,
                hatPattern: parseInt(colorData.hatPattern) === 1,
                farbAbhaengigkeitsArt: colorData.farbAbhaengigkeitsArt
            }
        )
    }

    public extractColorInfoFromEntwurf() {
        return this.model.entwurf.getColorObjects()
            .filter((c: Color) => c.isVisible && c.colorCode.indexOf("OO") === -1)
            .map(function (c: Color) {
                    return {
                        colorCode: c.colorCode,
                        colorCodeToDisplay: c.colorCodeToDisplay,
                        colorValue: c.colorValue,
                        palette: c.palette,
                        palettenCluster: c.palettenCluster,
                        blendToBackground: c.blendToBackground
                    }
                }
            );
    }

    public static convertColorsToDBFormat(colorObjects) {
        return colorObjects.filter(c => c.isUsedInCurrentDesign() || c.isOwayoOColorcode() || c.isConnectedToVisibleColorViaAlias(colorObjects))
            .map(c => ColorController.getColorDatastructureForBoundaryCrossing(c));
    }

    public static makeColorcodesWithAliasVisible(colorObjects: Color[], design: string) {
        return colorObjects.map(function (color: Color) {
            if (ColorCodeAliasTranslator.getColorcodesWithAlias(color.colorCodeToDisplay, design).length > 1) {
                color.isVisible = true;
            }
            return color;
        });
    }

    private static getColorDatastructureForBoundaryCrossing(color: Color) {
        const palette = PalettenLoader.getFlattenedPalettenCluster(color.palettenCluster);
        const colorInPalette = arrayElementByKeyValue(palette, "RGBHex", ColorUtils.decToHex(color.colorValue));
        let colorName: string;
        if (colorInPalette !== null) {
            colorName = colorInPalette.NameImWeb;
        } else {
            colorName = null;
        }
        return {
            bezeichnung: color.colorCode,
            colorRGB: color.colorValue,
            nameImWeb: colorName,
            palette: color.palette,
            hatPattern: color.hatPattern
        };
    }

    private static getLuminanceDeltaFromIntValues(firstColor: number, secondColor: number): number {
        const firstLuminance = ColorUtils.getLuminanceOfRgbObject(ColorUtils.getRGBObjectFromIntValue(firstColor));
        const secondLuminance = ColorUtils.getLuminanceOfRgbObject(ColorUtils.getRGBObjectFromIntValue(secondColor));
        return Math.abs(firstLuminance - secondLuminance);
    }

    public static getDeltaEFromIntValues(color1Int, color2Int) {
        return ColorUtils.getDeltaEFrom2HexStrings(ColorUtils.decToHex(color1Int), ColorUtils.decToHex(color2Int));
    }

    getColorCodeNumbersOfAliases(colorCodeNr: number): number[] {
        const colorObject = this.model.entwurf.getColorObjects().find(c => c.colorCodeNr === colorCodeNr);
        if (colorObject === undefined) {
            return [];
        }
        return ColorCodeAliasTranslator.getColorcodesWithAlias(colorObject.colorCodeToDisplay, this.model.entwurf.getDesign())
            .filter(c => c.colorCode !== colorObject.colorCode).map(a => {
                const aliasColorObject = this.model.entwurf.getColorObjects().find(c => c.colorCode === a.colorCode);
                if (aliasColorObject === undefined) {
                    return null;
                }
                return aliasColorObject.colorCodeNr;
            }).filter(v => v !== null);
    }

    private getPalettenClusterForColorCode(colorCode: string) {
        return new Promise((resolve, reject) => {
            const colorObject = this.model.entwurf.getColorObjectByColorCode(colorCode);
            if (colorObject === null) {
                reject();
            } else {
                resolve(colorObject.palettenCluster);
            }

        })

    }

    private changeProductColorToColorName(colorname: string) {
        const bwColor = this.model.entwurf.getColorObjectByColorCode("BW");
        if (bwColor === null) {
            return;
        }
        const palettenClusterName = bwColor.palettenCluster;
        const palettenCluster = globalsObject.resourceManager.getResourceByName("palettenCluster_" + palettenClusterName).data;
        for (const palette in palettenCluster) {
            const color = palettenCluster[palette].find(c => c.NameImWeb === colorname);
            if (color !== undefined) {
                EventVerteiler.broadcast("colorChangedEvent", {
                    colorCode: "BW",
                    colorHexValue: color.RGBHex,
                    byUser: false
                })
                globalsObject.resourceManager.executeInvalidation("colorValueWasSetBW")
            }
        }

    }

    public getPalettenClusternameForColorCode(colorCode: string) {
        const colorFromEntwurf = this.model.entwurf.getColorObjectByColorCode(colorCode);
        if (colorFromEntwurf === null) {
            const color = this.model.getCurrentOwayoProduct().colorCodes.find(c => c.colorCode === colorCode);
            return color?.PalettenCluster || null;
        }
        return colorFromEntwurf.palettenCluster;


    }
}
